import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, CircularProgress, DialogContent } from '@mui/material';
import styles from './AddWorkoutsDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import SearchBar from '../atoms/SearchBar';
import MultiSelectList from '../composites/MultiSelectList';
import { useSessionData } from '../../providers';
import {
  fetchTemplateById,
  fetchTemplatesPageByGymId,
} from '../../api';
import { queryKeys } from '../../api/queryKeys';

import type { TrainingRoutineTemplate } from '../../types';

const PAGE_SIZE_MAX = 100;

export interface AddWorkoutsDialogProps {
  onSubmit: (workouts: TrainingRoutineTemplate[]) => void;
  onClose: () => void;
  singleSelect?: boolean;
}

const AddWorkoutsDialog: React.FC<AddWorkoutsDialogProps> = ({
  onClose,
  onSubmit,
  singleSelect = false,
}) => {
  const { gym } = useSessionData();

  const { data: detailedWorkouts, isLoading } = useQuery<TrainingRoutineTemplate[]>({
    queryKey: queryKeys.templates.gymPage(gym?.id ?? '', {
      page: 0,
      size: PAGE_SIZE_MAX,
      search: '',
    }),
    queryFn: async () => {
      if (!gym?.id) {
        return [];
      }

      const allWorkouts: TrainingRoutineTemplate[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const gymResult = await fetchTemplatesPageByGymId(gym.id, {
          page,
          size: PAGE_SIZE_MAX,
          search: '',
        });

        if (!gymResult.success || !gymResult.data) {
          hasMore = false;
          break;
        }

        const content = gymResult.data.content ?? [];
        allWorkouts.push(...content);

        const { totalPages = 1 } = gymResult.data.meta ?? {};
        page += 1;
        hasMore = page < totalPages;
      }

      return allWorkouts;
    },
    enabled: Boolean(gym?.id),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const workouts: TrainingRoutineTemplate[] = detailedWorkouts ?? [];

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkouts, setSelectedWorkouts] = useState<
    TrainingRoutineTemplate[]
  >([]);

  const filteredWorkouts = workouts
    .filter(
      (workout): workout is TrainingRoutineTemplate =>
        Boolean(workout) && typeof workout.name === 'string',
    )
    .filter(({ name }) =>
      name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  return (
    <BaseDialog
      onClose={onClose}
      isOpen={true}
      title="Choose or Search for a Workout"
      actions={
        <Button
          className={styles.submit}
          disabled={isLoading}
          onClick={async () => {
            const fetchedWorkouts = await Promise.all(
              selectedWorkouts.map(async ({ id }) => {
                const result = await fetchTemplateById(id);
                return result.success ? result.data : null;
              }),
            );

            const fullSelectedWorkouts = fetchedWorkouts.filter(
              (workout): workout is TrainingRoutineTemplate =>
                Boolean(workout && workout.routine),
            );

            onSubmit(fullSelectedWorkouts);
          }}
        >
          <span className={styles.submitText}>Add</span>
        </Button>
      }
    >
      <DialogContent className={styles.addWorkoutsDialog}>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 220,
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            <SearchBar
              className={styles.search}
              placeholder="Search"
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
            <MultiSelectList
              items={filteredWorkouts}
              selectedItems={selectedWorkouts}
              setSelectedItems={setSelectedWorkouts}
              singleSelect={singleSelect}
              EmptyStateComponent={() => null}
              ItemComponent={({ data }) => (
                <div className={styles.templateMultiSelectItem}>
                  <div className={styles.templateName}>{data.name}</div>
                  <div className={styles.templateExercises}>
                    {(data.exerciseCount ?? 0)} Exercises
                  </div>
                </div>
              )}
            />
          </>
        )}
      </DialogContent>
    </BaseDialog>
  );
};

export default AddWorkoutsDialog;
